
/*
    TODO:
        Multiple IC's - each one has different application number / registration number
*/


export interface ITrademark {
    clientId?:                  string;

    referenceNumber:            string;
    title:                      string;
    imageUrl:                   string;             // URL of the image version of the mark
    internationalRegistration:  string;
    applicationNumber:          string;
    filingDate:                 number | null;      // AKA "Presentation Date"
    prioritySerialNumber:       string;
    priorityDate:               number | null;
    countryOfPriority:          string;
    classNumber:                string;
    descriptionOfGoods:         string;
    registrationNumber:         string;
    registrationDate:           number | null;      // AKA "Grant Date"
    expirationDate:             number | null;      // AKA "Termination Date"
    owners:                     string[];
    attorneyOfRecord:           string;
    lastRenewalDate:            number | null;      // AKA "Last Renewal Date"

    deadlines:                  IDeadline[];
}

export class TrademarkUtils {
    public static getDefault(title: string): ITrademark {
        return { 
            referenceNumber:            "",
            title:                      title,
            imageUrl:                   "",
            internationalRegistration:  "",
            applicationNumber:          "",
            filingDate:                 null,
            prioritySerialNumber:       "",
            priorityDate:               null,
            countryOfPriority:          "",
            classNumber:                "",
            descriptionOfGoods:         "",
            registrationNumber:         "",
            registrationDate:           null,
            expirationDate:             null,
            owners:                     [],
            attorneyOfRecord:           "",
            lastRenewalDate:            null,
            deadlines:                  []
        };
    }

    public static toCsvFormat(trademarks: ITrademark[], clients: { [key: string]: IClient }): any[] {
        return trademarks.map((tm) => {
            const clientName = (tm.clientId && clients[tm.clientId])
                ? clients[tm.clientId].agent
                : "";

            return {
                "Client": clientName,
                "Title": tm.title,
                "International Registration": tm.internationalRegistration,
                "Application Number": tm.applicationNumber,
                "Filing Date": (tm.filingDate) ? new Date(tm.filingDate).toLocaleDateString("en-US") : "",
                "Priority Serial Number": tm.prioritySerialNumber,
                "Priority Date": (tm.priorityDate) ? new Date(tm.priorityDate).toLocaleDateString("en-US") : "",
                "Country of Priority": tm.countryOfPriority,
                "Class Number": tm.classNumber,
                "Description of Goods or Services": tm.descriptionOfGoods,
                "Registration Number": tm.registrationNumber,
                "Registration Date": (tm.registrationDate) ? new Date(tm.registrationDate).toLocaleDateString("en-US") : "",
                "Expiration Date": (tm.expirationDate) ? new Date(tm.expirationDate).toLocaleDateString("en-US") : "",
                "Owners": (tm.owners) ? tm.owners.join("; ") : "",
                "Attorney of Record": tm.attorneyOfRecord,
                "Last Renewal Date": (tm.lastRenewalDate) ? new Date(tm.lastRenewalDate).toLocaleDateString("en-US") : "",
            };
        });
    }

    // Returns true if the given search text matches the given trademark
    public static searchMatchesTrademark(searchText: string, trademark: ITrademark): boolean {
        let hasMatch = false;

        // Search every property for the search text...
        Object.values(trademark).forEach((value) => {
            if (hasMatch) { return; }

            const sValue = value + "";
            if (sValue.toLowerCase().includes(searchText.toLowerCase())) {
                hasMatch = true;
                return;
            }
        });

        return hasMatch;
    }

    // Given a trademark, returns the validated deadlines for this trademark...
    public static validateDeadlines(trademark: ITrademark): IDeadline[] {
        const deadlines: IDeadline[] = [...trademark.deadlines];
        const typesToDueDates: { type: string, dueDate: Date }[] = [];

        //
        // RENEWAL BEGINS / RENEWAL DUE
        if (trademark.filingDate) {
            const filingDate = new Date(trademark.filingDate);

            const novFive2020 = new Date(2020, 10, 5);
            const isBeforeNovFive2020 = filingDate.getTime() < novFive2020.getTime();

            // If FilingDate < 11/5/2020, then expiration is based on FilingDate + 10 years...
            if (isBeforeNovFive2020) {

                // Renewal will be filingDate + 10 years...
                let newRenewalBegins = DateUtils.addMonthsToDate(filingDate, 114);
                let newRenewalDue = DateUtils.addMonthsToDate(filingDate, 120);

                // If there is a "Last Renewal Date", we must choose a date after that.
                // So keep adding 10 years until we pass the lastRenewalDate
                if (trademark.lastRenewalDate) {
                    const lastRenewalDate = new Date(trademark.lastRenewalDate);                    
                    while (lastRenewalDate.getTime() > newRenewalBegins.getTime()) {
                        newRenewalBegins = DateUtils.addMonthsToDate(newRenewalBegins, 120);
                        newRenewalDue = DateUtils.addMonthsToDate(newRenewalDue, 120);
                    }
                }
                
                // Add the Renwal Begins/Renewal Due deadlines...
                typesToDueDates.push({ type: "RENEWAL BEGINS",  dueDate: newRenewalBegins });
                typesToDueDates.push({ type: "RENEWAL DUE",     dueDate: newRenewalDue });
            } 
            // If FilingDate > 11/5/2020, then expiration is based on RegistrationDate + 10 years...
            else if (!isBeforeNovFive2020 && trademark.registrationDate) {
                // Renewal will be registrationDate + 10 years...
                const registrationDate = new Date(trademark.registrationDate);
                let newRenewalBegins = DateUtils.addMonthsToDate(registrationDate, 114);
                let newRenewalDue = DateUtils.addMonthsToDate(registrationDate, 120);

                // If there is a "Last Renewal Date", we must choose a date after that.
                // So keep adding 10 years until we pass the lastRenewalDate
                if (trademark.lastRenewalDate) {
                    const lastRenewalDate = new Date(trademark.lastRenewalDate);                    
                    while (lastRenewalDate.getTime() > newRenewalBegins.getTime()) {
                        newRenewalBegins = DateUtils.addMonthsToDate(newRenewalBegins, 120);
                        newRenewalDue = DateUtils.addMonthsToDate(newRenewalDue, 120);
                    }
                }

                // Add the Renwal Begins/Renewal Due deadlines...
                typesToDueDates.push({ type: "RENEWAL BEGINS",  dueDate: newRenewalBegins });
                typesToDueDates.push({ type: "RENEWAL DUE",     dueDate: newRenewalDue });
            }
        }

        //
        // FIRST DECLARATION OF USE EXPIRES
        if (trademark.registrationDate) {
            const registrationDate = new Date(trademark.registrationDate);
            const augTen2018 = new Date(2018, 7, 10);
            const isAfterAugTen2018 = registrationDate.getTime() >= augTen2018.getTime();

            if (isAfterAugTen2018) {
                const firstDecDueDate = DateUtils.addMonthsToDate(registrationDate, 39);
                typesToDueDates.push({ type: "FIRST DECLARATION OF USE EXPIRES", dueDate: firstDecDueDate });
            }
        }

        // ---

        // Update deadlines as needed...
        typesToDueDates.forEach((typeToDueDate) => {
           const deadline = deadlines.findIndex(x => x.title === typeToDueDate.type);
            if (deadline !== -1) {
                // Even though the deadline might be the same, the dueDate might be different (e.g. if a renewal changes to a new cycle)
                // So, if the date has changed, we will uncheck the checkbox if it was checked
                const isComplete = (deadlines[deadline].isComplete) && (deadlines[deadline].dueDate == typeToDueDate.dueDate.getTime());

                // Now update the existing deadline...
                deadlines[deadline].dueDate = typeToDueDate.dueDate.getTime();
                deadlines[deadline].isComplete = isComplete;
            } else {
                deadlines.push({ title: typeToDueDate.type, description: "", dueDate: typeToDueDate.dueDate.getTime(), isComplete: false });
            }            
        });

        return deadlines;
    }
}

export enum PatentType {
    Patent = 1,
    UtilityModel = 2,
    IndustrialDesign = 3
}

export enum PatentApplicationType {
    ParisConventionApplication = 1,
    PCTApplication = 2
}

export interface IPatent {
    clientId?:                  string;

    patentType?:                PatentType | "";
    applicationType?:           PatentApplicationType | "";
    referenceNumber:            string;
    title:                      string;
    applicationNumber:          string;
    filingDate:                 number | null;
    prioritySerialNumber:       string;
    priorityDate:               number | null;
    countryOfPriority:          string;
    internationalFilingDate:    number | null;
    registrationDate:           number | null;
    grantingNumber:             string;
    expirationDate:             number | null;
    owners:                     string[];
    nextAnnuitiesDue:           number | null;
    lastPaymentOfAnnunities:    number | null;
    attorneyOfRecord:           string;
    lastRenewalDate:            number | null;      // AKA "Last Renewal Date"
    
    deadlines:                  IDeadline[];
}

export class PatentUtils {
    public static getDefault(title: string): IPatent {
        return {
            patentType:                 PatentType.Patent,
            applicationType:            PatentApplicationType.PCTApplication,
            referenceNumber:            "",
            title:                      title,
            applicationNumber:          "",
            filingDate:                 null,
            prioritySerialNumber:       "",
            priorityDate:               null,
            countryOfPriority:          "",
            internationalFilingDate:    null,
            registrationDate:           null,
            grantingNumber:             "",
            expirationDate:             null,
            owners:                     [],
            nextAnnuitiesDue:           null,
            lastPaymentOfAnnunities:    null,
            attorneyOfRecord:           "",
            lastRenewalDate:            null,
            deadlines:                  []
        };
    }

    public static patentTypeToString(patentType: PatentType): string {
        switch (patentType) {
            case PatentType.Patent:
                return "Patent";
            case PatentType.UtilityModel:
                return "Utility Model";
            case PatentType.IndustrialDesign:
                return "Industrial Design";
            default:
                return "";
        }
    }

    public static applicationTypeToString(applicationType: PatentApplicationType): string {
        switch (applicationType) {
            case PatentApplicationType.ParisConventionApplication:
                return "Paris Convention Application";
            case PatentApplicationType.PCTApplication:
                return "PCT Application";
            default:
                return "";
        }
    }

    public static toCsvFormat(patents: IPatent[], clients: { [key: string]: IClient }): any[] {
        return patents.map((patent) => {
            const clientName = (patent.clientId && clients[patent.clientId])
                ? clients[patent.clientId].agent
                : "";

            return {
                "Client": clientName,
                "Patent Type": (patent.patentType) ? PatentUtils.patentTypeToString(patent.patentType) : "",
                "Application Type": (patent.applicationType) ? PatentUtils.applicationTypeToString(patent.applicationType) : "",
                "Reference Number": patent.referenceNumber,
                "Title": patent.title,
                "Application Number": patent.applicationNumber,
                "Filing Date": (patent.filingDate) ? new Date(patent.filingDate).toLocaleDateString("en-US") : "",
                "Priority Serial Number": patent.prioritySerialNumber,
                "Priority Date": (patent.priorityDate) ? new Date(patent.priorityDate).toLocaleDateString("en-US") : "",
                "Country of Priority": patent.countryOfPriority,
                "International Filing Date": (patent.internationalFilingDate) ? new Date(patent.internationalFilingDate).toLocaleDateString("en-US") : "",
                "Registration Date": (patent.registrationDate) ? new Date(patent.registrationDate).toLocaleDateString("en-US") : "",
                "Granting Number": patent.grantingNumber,
                "Expiration Date": (patent.expirationDate) ? new Date(patent.expirationDate).toLocaleDateString("en-US") : "",
                "Owners": (patent.owners) ? patent.owners.join("; ") : "",
                "Next Annuities Due": (patent.nextAnnuitiesDue) ? new Date(patent.nextAnnuitiesDue).toLocaleDateString("en-US") : "",
                "Last Payment of Annuities": (patent.lastPaymentOfAnnunities) ? new Date(patent.lastPaymentOfAnnunities).toLocaleDateString("en-US") : "",
                "Attorney of Record": patent.attorneyOfRecord
            };
        });
    }

    // Returns true if the given search text matches the given patent
    public static searchMatchesPatent(searchText: string, patent: IPatent): boolean {
        let hasMatch = false;

        // Search every property for the search text...
        Object.values(patent).forEach((value) => {
            if (hasMatch) { return; }

            const sValue = value + "";
            if (sValue.toLowerCase().includes(searchText.toLowerCase())) {
                hasMatch = true;
                return;
            }
        });

        return hasMatch;
    }

    // Given a patent, returns the validated deadlines for this patent...
    public static validateDeadlines(patent: IPatent): IDeadline[] {
        const deadlines: IDeadline[] = [...patent.deadlines];
        const typesToDueDates: { type: string, dueDate: Date }[] = [];

        //
        // NEXT ANNUITIES DUE
        if (patent.nextAnnuitiesDue) {
            typesToDueDates.push({ type: "NEXT ANNUITIES DUE", dueDate: new Date(patent.nextAnnuitiesDue) });
        }

        //
        // RENEWAL BEGINS / RENEWAL DUE (INDUSTRIAL DESIGN)
        // Industrial designs are renewed every 5 years up to 25 years...
        // We only want to add a renewal if it is "granted", so check if the granting number exists
        if (patent.patentType === PatentType.IndustrialDesign.toString()) {
            if (patent.filingDate && patent.grantingNumber) {
                const now = new Date();
                const filingDate = new Date(patent.filingDate);
                for(let i = 1; i <= 5; i++) {
                    const nextDueDate = DateUtils.addMonthsToDate(filingDate, i * 60);
                    const nextDueDateBegins = DateUtils.addMonthsToDate(nextDueDate, -6);

                    // Check if this due date period starts AFTER the last renewal date begins...
                    const isAfterLastRenewal = (patent.lastRenewalDate)
                        ? nextDueDateBegins > new Date(patent.lastRenewalDate)
                        : true;

                    // This must be both after the last renewal -AND- in the future...
                    if (isAfterLastRenewal && (nextDueDate > now)) {
                        typesToDueDates.push({ type: "RENEWAL BEGINS",  dueDate: DateUtils.addMonthsToDate(nextDueDate, -6) });
                        typesToDueDates.push({ type: "RENEWAL DUE",     dueDate: nextDueDate });
                        break;
                    }
                }
            }
        }

        // ---

        // Update deadlines as needed...
        typesToDueDates.forEach((typeToDueDate) => {
           const deadline = deadlines.findIndex(x => x.title === typeToDueDate.type);
            if (deadline !== -1) {
                // Even though the deadline might be the same, the dueDate might be different (e.g. if a renewal changes to a new cycle)
                // So, if the date has changed, we will uncheck the checkbox if it was checked
                const isComplete = (deadlines[deadline].isComplete) && (deadlines[deadline].dueDate == typeToDueDate.dueDate.getTime());

                // Now update the existing deadline...
                deadlines[deadline].dueDate = typeToDueDate.dueDate.getTime();
                deadlines[deadline].isComplete = isComplete;
            } else {
                deadlines.push({ title: typeToDueDate.type, description: "", dueDate: typeToDueDate.dueDate.getTime(), isComplete: false });
            }            
        });

        return deadlines;
    }
}

export interface IClient {
    agent:      string;
    attorney:   string;
    email:      string;
    country:    string;
}

export interface IDeadline {
    title:          string;
    description:    string;
    dueDate:        number | null;
    isComplete:     boolean;
}

export class DeadlineUtils {
    public static toCsvFormat(deadlines: { deadline: IDeadline, data: { title: string } }[]): any[] {
        return deadlines.map((deadlineWithTrademark) => {
            return {
                "Title": deadlineWithTrademark.data.title,
                "Action": deadlineWithTrademark.deadline.title,
                "Description": deadlineWithTrademark.deadline.description,
                "Due Date": deadlineWithTrademark.deadline.dueDate ? (new Date(deadlineWithTrademark.deadline.dueDate)).toLocaleDateString("en-US") : "Unknown"
            };
        });
    }
}

export class DateUtils {
    // Adds the number of months to the given date and returns a new date...
    public static addMonthsToDate(date: Date, numMonths: number): Date {
        const d = date.getDate();
        
        const nextDate = new Date(date);
        nextDate.setMonth(nextDate.getMonth() + numMonths);
        if (nextDate.getDate() !== d) {
            nextDate.setDate(0);
        }
        
        return nextDate;
    }

    public static formatDate(date: Date): string {
        return date.toDateString();
        //const options: Intl.DateTimeFormatOptions = { year: "numeric", month: "long", day: "numeric" };
        //return date.toLocaleDateString("en-US", options);
    }

    public static isToday(date: Date): boolean {
        const today = new Date();
        return (date.getDate() === today.getDate()) && (date.getMonth() === today.getMonth()) && (date.getFullYear() === today.getFullYear());
    }
}

export class Statistics {
    public static getNumApplicationsPerYear(trademarks: ITrademark[] | IPatent[]): { year: number, numApplications: number }[] {
        const yearsToNumApplications: { [key: number]: number } = {};

        // Count the number of applications per year...
        trademarks.forEach((trademark) => {
            if (!trademark.filingDate) { return; }

            const filingDate = new Date(trademark.filingDate);
            const year = filingDate.getFullYear();

            if (yearsToNumApplications[year]) {
                yearsToNumApplications[year]++;
            } else {
                yearsToNumApplications[year] = 1;
            }
        });

        // Convert to an array of { year, numApplications }...
        const yearsToNumApplicationsArray: { year: number, numApplications: number }[] = [];
        Object.keys(yearsToNumApplications).forEach((year: string) => {
            yearsToNumApplicationsArray.push({ year: parseInt(year), numApplications: yearsToNumApplications[parseInt(year)] });
        });

        // Sort by year...
        yearsToNumApplicationsArray.sort((a, b) => {
            if (a.year < b.year) { return -1; }
            if (a.year > b.year) { return 1; }
            return 0;
        });

        return yearsToNumApplicationsArray;
    }
}